import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { authClient } from '../lib/auth';

interface User {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  image: string | null;
  role?: string;
  isAnonymous?: boolean;
}

interface AnonymousAuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AnonymousAuthContextValue extends AnonymousAuthState {
  signInAnonymous: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AnonymousAuthContext = createContext<AnonymousAuthContextValue | null>(
  null
);

export function AnonymousAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AnonymousAuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });

  const signInAnonymous = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const result = await authClient.signIn.anonymous();
      if (result.error) {
        if (import.meta.env.DEV) {
          console.error('Anonymous sign-in failed:', result.error);
        }
        setState({
          user: null,
          isLoading: false,
          isAuthenticated: false,
        });
      } else {
        const userData = result.data?.user as User | undefined;
        setState({
          user: userData ?? null,
          isLoading: false,
          isAuthenticated: true,
        });
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Anonymous sign-in error:', error);
      }
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  }, []);

  const signOut = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      await authClient.signOut();
      // After sign out, sign in as anonymous again
      await signInAnonymous();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Sign out error:', error);
      }
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [signInAnonymous]);

  // Guard to prevent duplicate session checks (e.g. StrictMode double-mount)
  const hasCheckedSession = useRef(false);

  // Check for existing session on mount
  useEffect(() => {
    if (hasCheckedSession.current) return;
    hasCheckedSession.current = true;

    const checkSession = async () => {
      try {
        const session = await authClient.getSession();
        if (session.data?.user) {
          const userData = session.data.user as User;
          setState({
            user: userData,
            isLoading: false,
            isAuthenticated: true,
          });
        } else {
          // Auto-sign in as anonymous if no session exists
          await signInAnonymous();
        }
      } catch {
        // If session check fails, try anonymous sign-in
        await signInAnonymous();
      }
    };

    checkSession();
  }, [signInAnonymous]);

  return (
    <AnonymousAuthContext.Provider
      value={{
        ...state,
        signInAnonymous,
        signOut,
      }}
    >
      {children}
    </AnonymousAuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAnonymousAuth = () => {
  const context = useContext(AnonymousAuthContext);
  if (!context)
    throw new Error(
      'useAnonymousAuth must be used within AnonymousAuthProvider'
    );
  return context;
};
